/* eslint-disable no-unused-vars */
import ButtonBackHeader from "@/components/ui/Button/ButtonBackHeader";
import ParentSubpageShell from "@/components/Parent/ParentSubpageShell";
import nf, { nfRp } from "@/constants/nf";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import QuantityControl from "../Item/QuantityControl";
import { route } from "@/constants/routeName";

const AddOnModifier = ({ data, selected, onCheck, parentStyle }) => {
  if (parentStyle) {
    return (
      <label className="addon-option">
        <span>{data.name}</span>
        <small>
          {data.price ? `+${nfRp(data.price)}` : "Free"}
        </small>
        <Checkbox
          checked={!!selected}
          onChange={() => onCheck(data)}
          inputProps={{ "aria-label": data.name }}
        />
      </label>
    );
  }
  return (
    <div className="carts add-ons">
      <span>{data.name}</span>
      <span>{data.price ? `+${nf(data.price)}` : "Free"}</span>
      <Checkbox
        className="ck-table"
        checked={!!selected}
        onChange={() => onCheck(data)}
        inputProps={{ "aria-label": data.name }}
      />
    </div>
  );
};

export default function CartItem() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const item = useSelector((state) => state.cart.selectedItem);
  const servingTimes = useSelector((state) => state.content.servingTimes);
  const orderDate = useSelector((state) => state.cart.orderDate);
  const accountType = useSelector((state) => state.auth.accountType);
  const isParent = accountType === "parent";

  const [addOns, setAddOns] = useState(
    item?.add_ons?.map((a) => a.add_on) || []
  );
  const [modifiers, setModifiers] = useState(
    item?.modifiers?.map((a) => a.modifier) || []
  );
  const [servingTime, setServingTime] = useState(item?.serving_time?.id || "");
  const [amount, setAmount] = useState(item?.quantity || 1);
  const [notes, setNotes] = useState(item?.notes || "");

  const add_ons = item?.add_ons?.map((a) => a.add_on);
  const modifier = item?.modifiers?.map((a) => a.modifier);

  const isA = add_ons && add_ons?.length > 0;
  const isM = modifier && modifier?.length > 0;

  const onAddOns = (itm) => {
    if (addOns.find((a) => a.id === itm.id)) {
      setAddOns((prevState) => prevState.filter((a) => a.id !== itm.id));
    } else {
      setAddOns((prevState) => prevState.concat([itm]));
    }
  };
  const onAddModifiers = (itm) => {
    if (modifiers.find((a) => a.id === itm.id)) {
      setModifiers((prevState) => prevState.filter((a) => a.id !== itm.id));
    } else {
      setModifiers((prevState) => prevState.concat([itm]));
    }
  };
  const handleChangeServing = (event) => {
    setServingTime(event.target.value);
  };
  const changeAmount = (key) => {
    setAmount((prevState) => {
      if (key === "plus") {
        return prevState + 1;
      }
      if (prevState === 1) return 1;
      return prevState - 1;
    });
  };
  const getAddOnsModifier = () => {
    const newAddOns = addOns.map((a) => {
      return { id: uuidv4(), add_on: { ...a } };
    });
    const newModifiers = modifiers.map((a) => {
      return { id: uuidv4(), modifier: { ...a } };
    });
    return { addOns: newAddOns, modifiers: newModifiers };
  };
  const onAddToCart = () => {
    const { addOns, modifiers } = getAddOnsModifier();
    dispatch({
      type: "cart/updateItem",
      payload: {
        ...item,
        quantity: amount,
        is_void: false,
        modifiers: modifiers,
        add_ons: addOns,
        serving_time: servingTime
          ? servingTimes.find((s) => +s.id === +servingTime)
          : null,
        notes: notes,
        date: orderDate,
      },
    });
    navigate(-1);
  };

  let totalPrice = (+item?.selling_price || 0) * amount;
  if (addOns.length) {
    totalPrice += addOns.reduce((a, b) => +a + +b.price * amount, 0);
  }

  useEffect(() => {
    if (!item) {
      navigate(`/${route.menu}`);
    }
  }, [item, navigate]);

  const money = (n) => (isParent ? nfRp(n) : nf(n));
  const servingName =
    servingTimes?.find((s) => Number(s.id) === Number(servingTime))?.name ||
    "Meal";

  if (isParent) {
    return (
      <ParentSubpageShell
        title="Edit meal"
        backTo={`/${route.cart}`}
        backLabel="← Back to cart"
      >
        <div className="product-top">
          <h2 style={{ fontSize: 18, margin: 0, flex: 1 }}>{item?.name}</h2>
          <strong>{money(+item?.selling_price || 0)}</strong>
        </div>
        <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          {dayjs(item?.date).format("dddd, D MMMM YYYY")}
        </p>

        {(isA || isM) && (
          <section className="product-section">
            <h3>
              Add-ons <small className="muted">· Optional</small>
            </h3>
            {isA &&
              add_ons.map((a) => {
                const selected = addOns.find((m) => m.id === a.id);
                return (
                  <AddOnModifier
                    key={`add-ons-${a?.id}-${item?.uid}`}
                    data={a}
                    onCheck={onAddOns}
                    selected={selected}
                    parentStyle
                  />
                );
              })}
            {isM &&
              modifier.map((m) => {
                const selected = modifiers.find((a) => a.id === m.id);
                return (
                  <AddOnModifier
                    key={`modifier-${m?.id}-${item?.uid}`}
                    data={m}
                    onCheck={onAddModifiers}
                    selected={selected}
                    parentStyle
                  />
                );
              })}
          </section>
        )}

        <label className="product-section">
          <strong>
            Notes <small className="muted">· Optional</small>
          </strong>
          <textarea
            className="notes-field"
            placeholder="Add a note for this item"
            maxLength={500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            aria-label="Notes · Optional"
          />
        </label>

        <section className="product-section">
          <strong>Serving time</strong>
          <div className="service-status" style={{ marginTop: 8 }}>
            {servingTimes
              ?.filter((s) => s?.name && String(s.name).toLowerCase() !== "void")
              .map((s) => {
                const pressed = Number(s.id) === Number(servingTime);
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={pressed ? "selected" : undefined}
                    aria-pressed={pressed}
                    onClick={() => setServingTime(String(s.id))}
                  >
                    <strong>
                      {s.name}
                      {pressed ? " ✓" : ""}
                    </strong>
                  </button>
                );
              })}
          </div>
        </section>

        <div className="row product-section">
          <strong>Purchase amount</strong>
          <div className="qty">
            <button
              type="button"
              aria-label="Decrease quantity"
              disabled={amount <= 1}
              onClick={() => changeAmount("minus")}
            >
              −
            </button>
            <span>{amount}</span>
            <button
              type="button"
              aria-label="Increase quantity"
              onClick={() => changeAmount("plus")}
            >
              +
            </button>
          </div>
        </div>

        <div className="selection-summary">
          <strong>
            {item?.name} · {servingName}
          </strong>
          <div className="row">
            <span>Item total</span>
            <strong>{money(totalPrice)}</strong>
          </div>
        </div>

        <button
          type="button"
          className="main full"
          onClick={onAddToCart}
          disabled={amount === 0 || !servingTime}
        >
          Update cart · {money(totalPrice)}
        </button>
      </ParentSubpageShell>
    );
  }

  return (
    <div className="app-container app-page">
      <div className="relative flex flex-col flex-1 gap-6 p-4 pb-24">
        <ButtonBackHeader />
        <div className="relative flex flex-col items-center justify-center">
          <span className="font-bold">{item?.name}</span>
          <div>
            <span className="font-bold">{nf(+item?.selling_price)}</span>
          </div>
        </div>
        {(isA || isM) && (
          <div className="flex flex-col">
            {isA &&
              add_ons.map((a) => {
                const selected = addOns.find((m) => m.id === a.id);
                return (
                  <AddOnModifier
                    key={`add-ons-${a?.id}-${item?.uid}`}
                    data={a}
                    onCheck={onAddOns}
                    selected={selected}
                  />
                );
              })}
            {isM &&
              modifier.map((m) => {
                const selected = modifiers.find((a) => a.id === m.id);
                return (
                  <AddOnModifier
                    key={`modifier-${m?.id}-${item?.uid}`}
                    data={m}
                    onCheck={onAddModifiers}
                    selected={selected}
                  />
                );
              })}
          </div>
        )}
        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />
        <FormControl>
          <FormLabel id="demo-controlled-radio-buttons-group">
            Serving Times
          </FormLabel>
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={servingTime}
            onChange={handleChangeServing}
          >
            {servingTimes?.map((s) => {
              return (
                <FormControlLabel
                  key={s.id}
                  value={s.id}
                  control={<Radio />}
                  label={s.name}
                />
              );
            })}
          </RadioGroup>
        </FormControl>
        <div className="flex justify-between">
          <span>Purchase Amount</span>
          <QuantityControl
            amount={amount}
            changeAmount={changeAmount}
            disabled={amount === 0}
          />
        </div>
        <div className="flex justify-between">
          <span>Order Date</span>
          <span>{dayjs(item?.date).format("dddd, DD MMMM YYYY")}</span>
        </div>
        <button
          className="btn-actions"
          onClick={onAddToCart}
          disabled={amount === 0 || !servingTime}
        >
          Update Cart
          <span className="ml-1">{nf(totalPrice)}</span>
        </button>
      </div>
    </div>
  );
}
